/**
 * eventsExport — Smart Admin replacement R4 (2026-05-17).
 *
 * Per-row transform + column header set for the Events list export.
 * Audit verdict Decision 5: 21-column set with plural Affiliate ID(s)
 * and Facility ID(s) since events can be multi-org. Comma-separated
 * is acceptable for R4.
 *
 * Verified Members Count and Close Rate % are intentionally OMITTED
 * per audit must-fix #1 — including them would require an N+1 RPC
 * call per event during export (the R3 get_event_close_rate_stats
 * RPC is single-event by design). R5 will introduce a bulk stats RPC
 * keyed by event IDs and add those columns. For R4 the operator can
 * still see them per-event on EventDetail.
 *
 * Sales hierarchy columns currently display position IDs; R5 will add
 * a bulk join helper to resolve names. For R4 we surface the IDs +
 * names where available via inline lookups.
 */

import type { Event } from "../../types/event.types";
import type {
  EventCloseRateStatsRow,
  PositionNameRow,
} from "../../services/reportsSuite.service";
import type { UUID } from "../../types/common.types";
import {
  formatDailyHours,
  resolveDailyHours,
  type DayHours,
} from "../../utils/eventDayHours";

export const EVENTS_EXPORT_COLUMNS = [
  "Event ID",
  "Event Name",
  "Event Date",
  "End Date",
  "Type",
  "Status",
  "State",
  "Promo Code",
  "Location Name",
  "Location Address",
  "Organization Name(s)",
  "Affiliate ID(s)",
  "Facility ID(s)",
  "Attendance Count",
  "Verified Members",
  "Close Rate %",
  "Lead/Event Rep",
  "Territory Manager",
  "Regional Manager",
  "Sales Director",
  "Voucher Count",
  "Cancellation Reason",
  "Cancellation Notes",
  "Created At",
  "Updated At",
  // 2026-10-27 (migration 1120), appended so existing column positions do not move: the hours set for each day of
  // a multi-day event, event-local time ("Sat, Oct 24: 9 AM–5 PM · Sun, Oct 25: 9 AM–3 PM"); blank when not set.
  "Daily Hours",
] as const;

type ExportRow = Record<
  (typeof EVENTS_EXPORT_COLUMNS)[number],
  string | number
>;

const formatDateISO = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  // Strip time + zone for display in spreadsheet
  return iso.split("T")[0] ?? "";
};

const formatAddressFlat = (
  addr: Event["location_address"] | null | undefined,
  line1?: string | null,
  city?: string | null,
  state?: string | null,
  postal?: string | null,
): string => {
  if (typeof addr === "object" && addr) {
    const parts = [
      addr.address_line1,
      addr.city,
      addr.state,
      addr.postal_code,
    ].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(", ");
    }
  }
  // Fall through to the flat columns
  const parts = [line1, city, state, postal].filter(Boolean);
  return parts.join(", ");
};

// Loose typing — getEvents returns events with joined fields the
// Event type doesn't strictly enumerate. Use a flexible record shape.
export type EventLike = Event & {
  location_state?: string | null;
  location_city?: string | null;
  location_postal_code?: string | null;
  location_address_line1?: string | null;
  // Possible joined fields (may be absent depending on the query):
  cta_count?: number;
};

export interface EventRowToExportHydration {
  // R5 (2026-05-17): if both maps are provided, hierarchy + verified +
  // close-rate columns are populated. Without them the function falls
  // back to R4 behavior (IDs / blanks) for safety.
  statsByEventId?: Map<UUID, EventCloseRateStatsRow>;
  positionsById?: Map<UUID, PositionNameRow>;
  // 2026-10-26: event id -> promo code (get_event_promo_codes), the same value the list's
  // Promo Code column shows. Events without one export an empty string.
  promoCodesByEventId?: Record<string, string>;
  // 2026-10-27: event id -> saved hours each day (event_day_hours). Shown only when they still fit the event.
  dayHoursByEventId?: Map<string, DayHours[]>;
}

function positionDisplay(
  positionId: string | null | undefined,
  positionsById?: Map<UUID, PositionNameRow>,
): string {
  if (!positionId) {
    return "";
  }
  if (!positionsById) {
    return positionId;
  }
  const p = positionsById.get(positionId);
  if (!p) {
    return positionId;
  }
  const label = p.assigned_name || p.title;
  return p.is_active ? label : `${label} (inactive)`;
}

export function eventRowToExport(
  e: EventLike,
  hydration?: EventRowToExportHydration,
): ExportRow {
  // R4 audit Decision 2: state display falls back across columns for
  // legacy rows that never populated location_state.
  const stateDisplay =
    e.location_state ||
    (typeof e.location_address === "object"
      ? e.location_address?.state || ""
      : "") ||
    e.state ||
    "";

  const stats = hydration?.statsByEventId?.get(e.id);
  const verifiedMembers = stats?.verified_member_count ?? "";
  const closeRate =
    stats?.close_rate !== null && stats?.close_rate !== undefined
      ? (stats.close_rate * 100).toFixed(2)
      : "";
  const attendance = stats?.attendance_count ?? e.attendance_count ?? "";

  return {
    "Event ID": e.id ?? "",
    "Event Name": e.title ?? "",
    "Event Date": formatDateISO(e.start_time),
    "End Date": formatDateISO(e.end_time),
    Type: e.type ?? "",
    Status: e.status ?? "",
    State: stateDisplay,
    "Promo Code": hydration?.promoCodesByEventId?.[e.id] ?? "",
    "Location Name": e.location_name ?? "",
    "Location Address": formatAddressFlat(
      e.location_address,
      e.location_address_line1,
      e.location_city,
      e.location_state,
      e.location_postal_code,
    ),
    // Multi-org fields still deferred (would need bulk org lookup RPC).
    // R5 wave 2 territory or later.
    "Organization Name(s)": "",
    "Affiliate ID(s)": "",
    "Facility ID(s)": "",
    "Attendance Count": attendance,
    "Verified Members": verifiedMembers,
    "Close Rate %": closeRate,
    "Lead/Event Rep": positionDisplay(
      e.sales_position_id,
      hydration?.positionsById,
    ),
    "Territory Manager": positionDisplay(
      e.territory_manager_position_id,
      hydration?.positionsById,
    ),
    "Regional Manager": positionDisplay(
      e.regional_manager_position_id,
      hydration?.positionsById,
    ),
    "Sales Director": positionDisplay(
      e.sales_director_position_id,
      hydration?.positionsById,
    ),
    "Voucher Count": e.cta_count ?? "",
    "Cancellation Reason": e.cancellation_reason ?? "",
    "Cancellation Notes": e.cancellation_notes ?? "",
    "Created At": formatDateISO(e.created_at),
    "Updated At": formatDateISO(e.updated_at),
    "Daily Hours": (() => {
      const days = resolveDailyHours(
        e,
        hydration?.dayHoursByEventId?.get(e.id),
      );
      return days ? formatDailyHours(days) : "";
    })(),
  };
}
